/**
 * The GIS session, as React sees it.
 *
 * A thin adapter over `gis.ts` and nothing more. The session itself lives at
 * module scope on purpose — refresh timers must outlive any component, and a
 * StrictMode double-mount must not arm two of them — so this file's only jobs
 * are to subscribe, to re-render, and to hand the token's claims to the shell
 * for a name in the corner while `GET /me` is still in flight.
 *
 * What it deliberately does **not** do is decide anything. The claims here are
 * unverified (see jwt.ts); roles and `isAdmin` come from `GET /me`, and the
 * API re-checks both regardless of what this context believes.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { GOOGLE_CLIENT_ID, isAuthConfigured } from '../config';
import { currentSession, initAuth, signOut as gisSignOut, subscribe } from './gis';
import type { Session } from './gis';

interface AuthState {
  session: Session | null;
  isSignedIn: boolean;
  /** True until the first restore-or-not answer. Guards the signed-out flash. */
  isLoading: boolean;
  /** GIS could not load or is unconfigured — sign-in cannot work at all. */
  error: string | null;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => currentSession());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Subscribe *before* init: a token restored from IndexedDB is set during
    // `initAuth`, and a late subscription would miss that notification and sit
    // on a stale `null` until the next refresh.
    const unsubscribe = subscribe(setSession);

    if (!isAuthConfigured()) {
      setError('Sign-in is not configured for this build.');
      setIsLoading(false);
      return unsubscribe;
    }

    initAuth(GOOGLE_CLIENT_ID)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Sign-in failed to load'))
      .finally(() => setIsLoading(false));

    return unsubscribe;
  }, []);

  const signOut = useCallback(() => {
    gisSignOut();
    /**
     * Drop every cached query on the way out.
     *
     * Without this, the next person to sign in on this browser sees the
     * previous user's garages painted from cache while `/me` loads — their
     * data, briefly, on someone else's screen. `clear()` rather than
     * `invalidateQueries()`: invalidation keeps the stale data around to show
     * while refetching, which is precisely the thing to avoid here.
     */
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ session, isSignedIn: session !== null, isLoading, error, signOut }),
    [session, isLoading, error, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth() used outside <AuthProvider>');
  return context;
}
